import { audio } from '../systems/audio/AudioSystem.js';
import { requestLandscape } from '../core/deviceMode.js';
import { formatTime } from '../core/formatTime.js';

/** Total parent levels still needed before a Harmonics recipe unlocks — the sort key for "closest first". */
function remainingLevels(row) {
  return row.parents.reduce((sum, p) => sum + Math.max(0, p.required - p.level), 0);
}

/** Player-triggered pause screen (Escape/P) — Game.js drives visibility via game:paused/game:resumed. */
export class PauseOverlay {
  constructor(eventBus, root, { onResume, onExit }) {
    this.root = root;
    this.onResume = onResume;
    this.onExit = onExit;
    this.el = null;
    this._onFullscreenChange = null;
    eventBus.on('game:paused', (context) => this._show(context));
    eventBus.on('game:resumed', () => this._hide());
  }

  _show(context) {
    if (this.el) return;
    const el = document.createElement('div');
    el.className = 'levelup-overlay';
    el.innerHTML = `<div class="levelup-title">Paused</div>`;

    // Run snapshot from Game.js's game:paused payload — who/where/how long,
    // so a player returning from a break re-orients at a glance.
    if (context) {
      const strip = document.createElement('div');
      strip.className = 'pause-context';
      strip.innerHTML = `
        <span class="pause-chip pause-chip-champ" style="--champ: ${context.characterColor ?? 'var(--amber)'}">${context.characterName}</span>
        <span class="pause-chip">Lv ${context.level}</span>
        <span class="pause-chip">Boss ${context.bossNumber}/${context.bossCount}</span>
        <span class="pause-chip">${formatTime(context.survivalTime)}</span>
        <span class="pause-chip">${context.mode}</span>
      `;
      el.appendChild(strip);
    }

    if (context?.build) {
      const build = this._buildPanel(context.build);
      if (build) el.appendChild(build);
    }

    const resume = document.createElement('button');
    resume.type = 'button';
    resume.className = 'menu-button menu-button-primary';
    resume.textContent = 'Resume';
    resume.addEventListener('click', () => {
      audio.playSfx('click');
      this.onResume();
    });
    el.appendChild(resume);

    // Only real Fullscreen mode is immune to an OS taskbar/Dock/menu bar
    // popping up over the game (see style.css's #app safety-frame comment)
    // — this is the one place mid-run to reach it, since MainMenu's own
    // fullscreen button isn't visible once a run has started.
    const fullscreen = document.createElement('button');
    fullscreen.type = 'button';
    fullscreen.className = 'menu-button';
    const updateFullscreenLabel = () => {
      fullscreen.textContent = document.fullscreenElement ? 'Exit Fullscreen' : 'Fullscreen';
    };
    updateFullscreenLabel();
    fullscreen.addEventListener('click', () => {
      audio.playSfx('click');
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen().then(() => requestLandscape()).catch(() => {});
      }
    });
    el.appendChild(fullscreen);
    this._onFullscreenChange = updateFullscreenLabel;
    document.addEventListener('fullscreenchange', this._onFullscreenChange);

    // Abandons the run and returns to the main menu. Echo/Shard bonuses
    // already banked per boss kill are kept; the cumulative end-of-run
    // calcEchoes() payout is forfeited (only _endRun grants it).
    const exit = document.createElement('button');
    exit.type = 'button';
    exit.className = 'menu-button';
    exit.textContent = 'Exit Run';
    exit.addEventListener('click', () => {
      audio.playSfx('click');
      this.onExit();
    });
    el.appendChild(exit);

    const hint = document.createElement('div');
    hint.className = 'pause-hint';
    hint.textContent = 'WASD / Arrows move · Shift dash · Hold C focus · Space ultimate · Esc resume';
    el.appendChild(hint);

    this.root.appendChild(el);
    this.el = el;
  }

  /**
   * The run's current build: weapons with their levels, then the passives
   * and pacts picked at level-ups. Pause is the only moment in a run where
   * there's time to read any of it — mid-fight the HUD only has room for
   * weapon icons, so "what did I actually take?" was otherwise unanswerable
   * without remembering every pick screen.
   *
   * Returns null when there's nothing to show (a run paused before the first
   * pick) rather than an empty panel.
   */
  _buildPanel({ weapons = [], passives = [], pacts = [], fusedAway = [], fusions = [] }) {
    if (weapons.length === 0 && passives.length === 0 && pacts.length === 0) return null;

    const panel = document.createElement('div');
    panel.className = 'pause-build';

    const section = (title, rows) => {
      if (rows.length === 0) return;
      const heading = document.createElement('div');
      heading.className = 'pause-build-heading';
      heading.textContent = title;
      panel.appendChild(heading);
      const list = document.createElement('div');
      list.className = 'pause-build-list';
      for (const row of rows) list.appendChild(row);
      panel.appendChild(list);
    };

    section('Weapons', weapons.map(({ name, level, maxLevel }) => {
      const row = document.createElement('div');
      row.className = 'pause-build-row';
      const atMax = level >= maxLevel;
      row.innerHTML = `
        <span class="pause-build-name">${name}</span>
        <span class="pause-build-level${atMax ? ' pause-build-level-max' : ''}">${atMax ? 'MAX' : `Lv ${level}`}</span>
      `;
      return row;
    }));

    section('Upgrades', passives.map(({ label, count }) => {
      const row = document.createElement('div');
      row.className = 'pause-build-row';
      row.innerHTML = `
        <span class="pause-build-name">${label}</span>
        ${count > 1 ? `<span class="pause-build-level">x${count}</span>` : ''}
      `;
      return row;
    }));

    // Pacts carry a drawback, so they're listed apart from plain upgrades
    // with the cost spelled out — a pact you forgot you took is exactly the
    // thing you want to re-read when a run starts going wrong.
    section('Pacts', pacts.map(({ label, boonText, costText }) => {
      const row = document.createElement('div');
      row.className = 'pause-build-row pause-build-row-pact';
      row.innerHTML = `
        <span class="pause-build-name">${label}</span>
        <span class="pause-build-pact-detail">${boonText ?? ''}${costText ? ` <span class="pause-build-cost">${costText}</span>` : ''}</span>
      `;
      return row;
    }));

    // Harmonics recipes, locked ones included (BULLET-HEAVEN.md §H4). A
    // fusion is the biggest single jump a run can take, and it used to be
    // invisible until the card appeared — so nobody could steer toward one.
    // Showing the recipe with live parent progress ("Nova Pulse 6/6 + Orbit
    // Ward 4/6") turns the next few picks into a decision instead of a
    // shrug. Ordered eligible-first, then by how close the recipe is, so the
    // thing worth chasing is at the top rather than buried in the list.
    const recipes = [...fusions].sort((a, b) => {
      const rank = (row) => (row.owned ? 2 : row.eligible ? 0 : 1);
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      return remainingLevels(a) - remainingLevels(b);
    });
    section('Harmonics', recipes.map((row) => {
      const el = document.createElement('div');
      el.className = 'pause-build-row pause-build-row-fusion'
        + (row.owned ? ' pause-build-row-fused' : row.eligible ? ' pause-build-row-ready' : ' pause-build-row-locked');
      const parents = row.parents
        .map((p) => `${p.name} ${Math.min(p.level, p.required)}/${p.required}`)
        .join(' + ');
      el.innerHTML = `
        <span class="pause-build-name">${row.name}</span>
        <span class="pause-build-fusion-detail">${row.owned ? 'fused' : parents}</span>
        ${row.eligible && !row.owned ? '<span class="pause-build-level pause-build-level-max">READY</span>' : ''}
      `;
      return el;
    }));

    // Only present after a Harmonics fusion — the parents are gone from the
    // weapon list, and without a note the levels invested in them look lost.
    if (fusedAway.length > 0) {
      const note = document.createElement('div');
      note.className = 'pause-build-note';
      note.textContent = `Fused away: ${fusedAway.join(', ')}`;
      panel.appendChild(note);
    }

    return panel;
  }

  _hide() {
    this.el?.remove();
    this.el = null;
    if (this._onFullscreenChange) {
      document.removeEventListener('fullscreenchange', this._onFullscreenChange);
      this._onFullscreenChange = null;
    }
  }

  /** Covers the "exited mid-pause" path (Exit Run), where game:resumed never fires to trigger _hide() itself. */
  destroy() {
    this._hide();
  }
}
